import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Tile } from '~/ui/core/tile/Tile';

import { Title } from '../components/SidepanelTitle';
import { useIsSkillProficiencyVisible } from '../job-profile-section/skills/skill-proficiency-visibility';
import { ComparisonHeader, ProfileComparisonTable } from '../profile-comparison/ProfileComparisonTable';
import { useComparisonProficiencySettings } from '../profile-comparison/use-comparison-proficiency-settings';
import { ActiveLanguageProvider, useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useNavigateToStandaloneProfile } from '../url-navigation/use-navigate-to-standalone-profile';
import { JSA_ROLE_COMPARISON_MAX_ROLES } from '../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { AddRolePopover } from './AddRolePopover';
import { RoleComparisonHeaderCell } from './RoleComparisonHeaderCell';
import { type ComparedItem, useRoleComparisonData } from './use-role-comparison-data';

const ADD_ITEM_COLUMN_WIDTH = '10rem';

type ItemType = 'job-level' | 'job-role';

const RoleComparisonContent: FunctionComponent<{
    readonly architectureId: string;
    readonly comparedItems: readonly ComparedItem[];
    readonly itemType: ItemType;
    readonly openProfilesOnStandalonePage: boolean;
    readonly showProficiency: boolean | undefined;
    readonly onClose: () => void;
    readonly onSelectItemVersion: (itemId: string, version: string) => void;
}> = ({
    architectureId,
    comparedItems,
    itemType,
    onClose,
    onSelectItemVersion,
    openProfilesOnStandalonePage,
    showProficiency,
}) => {
    const translate = useTranslate();
    const { activeLanguage, setActiveLanguage } = useActiveLanguage();
    const { navigate: navigateToStandaloneProfile } = useNavigateToStandaloneProfile();
    const {
        openJobProfileTile,
        openSkillTile,
        updateRoleComparisonTileItems,
        updateRoleComparisonTileLanguage,
        updateRoleComparisonTileProficiency,
    } = useJobArchitectureUrlTileActions();
    const comparisonProficiencySettings = useComparisonProficiencySettings({
        tileShowProficiency: showProficiency,
        updateTileProficiency: updateRoleComparisonTileProficiency,
    });
    const isProficiencyVisible = useIsSkillProficiencyVisible(itemType);
    const proficiencySettings = isProficiencyVisible
        ? comparisonProficiencySettings
        : { ...comparisonProficiencySettings, highlightProficiency: false, showProficiency: false };
    const itemIds = useMemo(() => comparedItems.map(({ id }) => id), [comparedItems]);
    const canAddItem = itemIds.length < JSA_ROLE_COMPARISON_MAX_ROLES;
    const canRemoveItem = itemIds.length > 1;
    const title = translate(
        itemType === 'job-role'
            ? 'pages.job-architecture-v2.job-profile-compare-roles'
            : 'pages.job-architecture-v2.job-profile-compare-levels',
    );

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA): void => {
            setActiveLanguage(language);
            updateRoleComparisonTileLanguage(language);
        },
        [setActiveLanguage, updateRoleComparisonTileLanguage],
    );

    const openProfile = useCallback(
        (itemId: string): void => {
            const details = comparedItems.find(({ id }) => id === itemId)?.details;

            if (openProfilesOnStandalonePage) {
                navigateToStandaloneProfile({
                    architectureId,
                    itemId,
                    itemType,
                    preferredLanguage: activeLanguage,
                    version: details?.versionInfo?.version,
                });

                return;
            }

            openJobProfileTile({
                mode: 'view',
                preferredLanguage: activeLanguage,
                profileId: itemId,
                type: itemType,
                version: details?.versionInfo?.version,
            });
        },
        [
            activeLanguage,
            architectureId,
            comparedItems,
            itemType,
            navigateToStandaloneProfile,
            openJobProfileTile,
            openProfilesOnStandalonePage,
        ],
    );

    return (
        <Tile className="gap-0" data-testid="role-comparison">
            <div className="flex w-full shrink-0 flex-col gap-s px-6xl pb-xxl pt-tile-edge">
                <ComparisonHeader
                    proficiencySettings={isProficiencyVisible ? proficiencySettings : null}
                    onClose={onClose}
                    onLanguageChange={handleLanguageChange}
                />
                <Title>{title}</Title>
            </div>
            <div className="min-h-0 w-full min-w-0 max-w-full flex-1 overflow-hidden px-6xl pb-xxl">
                <ProfileComparisonTable
                    columns={comparedItems}
                    emptyMessage={translate(
                        itemType === 'job-role'
                            ? 'pages.job-architecture-v2.job-profile-role-comparison-no-skills'
                            : 'pages.job-architecture-v2.job-profile-level-comparison-no-skills',
                    )}
                    label={title}
                    proficiencySettings={proficiencySettings}
                    renderHeaderCell={(column) => {
                        const comparedItem = comparedItems.find(({ id }) => id === column.id);

                        return is.undefined(comparedItem) ? null : (
                            <RoleComparisonHeaderCell
                                canRemove={canRemoveItem}
                                comparedItem={comparedItem}
                                itemType={itemType}
                                onOpen={openProfile}
                                onRemove={(itemId) =>
                                    updateRoleComparisonTileItems(itemIds.filter((id) => id !== itemId))
                                }
                                onSelectVersion={onSelectItemVersion}
                            />
                        );
                    }}
                    trailingColumn={
                        canAddItem
                            ? {
                                  header: (
                                      <AddRolePopover
                                          architectureId={architectureId}
                                          defaultOpen={itemIds.length === 1}
                                          excludedItemIds={itemIds}
                                          itemType={itemType}
                                          onAddItem={(itemId) => updateRoleComparisonTileItems([...itemIds, itemId])}
                                      />
                                  ),
                                  width: ADD_ITEM_COLUMN_WIDTH,
                              }
                            : undefined
                    }
                    onOpenSkill={(skillId) => openSkillTile(skillId, activeLanguage)}
                />
            </div>
        </Tile>
    );
};

export const RoleComparison: FunctionComponent<{
    readonly architectureId: string;
    readonly itemIds: readonly string[];
    readonly itemType: ItemType;
    readonly openProfilesOnStandalonePage: boolean;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly showProficiency?: boolean;
    readonly onClose: () => void;
}> = ({
    architectureId,
    itemIds,
    itemType,
    onClose,
    openProfilesOnStandalonePage,
    preferredLanguage,
    showProficiency,
}) => {
    const { items, selectItemVersion } = useRoleComparisonData(architectureId, itemType, itemIds);
    const profiles = useMemo(() => items.flatMap(({ details }) => (is.undefined(details) ? [] : [details])), [items]);

    return (
        <ActiveLanguageProvider preferredLanguage={preferredLanguage} profiles={profiles}>
            <RoleComparisonContent
                architectureId={architectureId}
                comparedItems={items}
                itemType={itemType}
                openProfilesOnStandalonePage={openProfilesOnStandalonePage}
                showProficiency={showProficiency}
                onClose={onClose}
                onSelectItemVersion={selectItemVersion}
            />
        </ActiveLanguageProvider>
    );
};
